import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import Empty from 'components/Empty';
import LoadMore, { LoadMoreProps } from 'components/LoadMore';
import { ReactElement, useLayoutEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { currentReviewProfileState } from '../hooks/useReview';
import Profile from './Profile';
import { useVirtualList } from 'ahooks';

const Container = styled.div`
  height: 100%;
  overflow: auto;
  > ul {
    margin: 0;
    padding: 0;
    list-style: none;
    > li {
      border-bottom: solid 1px var(--fett-border-default);
    }
  }
`;

let globalScrollTop = 0;

export interface ProfileListProps extends Util.PartialPartial<LoadMoreProps, 'children'> {
  isGlobal?: boolean;
  list: Review.Profile[];
  onStatusChange: (profile: Review.Profile, status: Review.Status) => void;
  emptyRender?: () => ReactElement;
}

function ProfileList(props: ProfileListProps) {
  const {
    isGlobal = false,
    list,
    onStatusChange,
    hasMore,
    emptyRender = () => <Empty />,
    ...otherProps
  } = props;

  const isEmpty = !hasMore && list.length === 0;

  const containerRef = useRef<HTMLDivElement>(null);

  const wrapperRef = useRef<HTMLUListElement>(null);

  const currentReview = useAtomValue(currentReviewProfileState);

  const setCurrentReview = useSetAtom(currentReviewProfileState);

  const [vList] = useVirtualList(list, {
    containerTarget: containerRef,
    wrapperTarget: wrapperRef,
    overscan: 8,
    itemHeight: (i) => (list[i].buyerComment ? 85 : 61),
  });

  useLayoutEffect(() => {
    if (!isGlobal || !containerRef.current) {
      return () => {};
    }

    const { current: container } = containerRef;

    container.scrollTo({
      top: globalScrollTop,
    });

    const handler = () => {
      globalScrollTop = container.scrollTop;
    };

    container.addEventListener('scroll', handler);

    return () => {
      container.removeEventListener('scroll', handler);
    };
  }, [isGlobal]);

  if (isEmpty) {
    return emptyRender();
  }

  const displayList = vList.length ? vList.map(({ data: profile }) => profile) : list;

  return (
    <LoadMore hasMore={hasMore} {...otherProps}>
      {(loadEle) => {
        const list = (
          <ul ref={wrapperRef}>
            {displayList.map((profile) => {
              const { commentId } = profile;

              const active = Boolean(currentReview && currentReview.commentId === commentId);

              return (
                <li key={commentId}>
                  <Profile
                    active={active}
                    profile={profile}
                    onStatusChange={onStatusChange}
                    onClick={() => {
                      setCurrentReview(profile);

                      if (currentReview) {
                        posthog.capture('$review', {
                          type: 'switchReview',
                        });
                      }
                    }}
                  />
                </li>
              );
            })}
            {loadEle}
          </ul>
        );

        return <Container ref={containerRef}>{list}</Container>;
      }}
    </LoadMore>
  );
}

export default ProfileList;
